import { Box } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import React, { useId } from "react";

// Solid colors only: each tone is used for a border and a color-mix tint.
// Overwriting runs read as a warning; safe runs read as blue.
const TONE_COLORS = {
    default: "var(--color-link-text)",
    info: "#1976D2",
    warning: "#C26A00",
};

/**
 * Radio cards for choosing how a task runs (e.g. soft vs. hard).
 *
 * Each option is a native radio inside a label, so arrow keys, Tab and screen
 * readers behave like a regular radio group.
 *
 * options: [{ value, title, description, icon, tone?: "default" | "info" | "warning" }]
 */
const ModeCards = ({ value, onChange, options, ariaLabel, disabled = false }) => {
    const name = useId();

    return (
        <Box
            role="radiogroup"
            aria-label={ariaLabel}
            sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
                gap: 1,
                width: "100%",
                maxWidth: 380,
            }}
        >
            {options.map((option) => {
                const selected = option.value === value;
                const accent = TONE_COLORS[option.tone] || TONE_COLORS.default;
                const Icon = option.icon;
                const descriptionId = `${name}-${option.value}-description`;

                return (
                    <Box
                        key={option.value}
                        component="label"
                        sx={{
                            position: "relative",
                            display: "flex",
                            gap: 1,
                            alignItems: "flex-start",
                            minHeight: 44,
                            p: "10px 12px",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid",
                            borderColor: selected ? accent : "#D0D0D0",
                            boxShadow: selected ? `inset 0 0 0 1px ${accent}` : "none",
                            backgroundColor: selected
                                ? `color-mix(in srgb, ${accent} 7%, #fff)`
                                : "#fff",
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.55 : 1,
                            textAlign: "left",
                            transition:
                                "border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease",
                            "&:hover": disabled || selected
                                ? undefined
                                : { borderColor: "#9E9E9E" },
                            "&:has(input:focus-visible)": {
                                outline: `2px solid ${accent}`,
                                outlineOffset: 2,
                            },
                            "@media (prefers-reduced-motion: reduce)": {
                                transition: "none",
                            },
                        }}
                    >
                        <Box
                            component="input"
                            type="radio"
                            name={name}
                            value={option.value}
                            checked={selected}
                            disabled={disabled}
                            onChange={() => onChange(option.value)}
                            aria-describedby={descriptionId}
                            sx={{
                                position: "absolute",
                                opacity: 0,
                                width: 1,
                                height: 1,
                                m: 0,
                                pointerEvents: "none",
                            }}
                        />
                        {Icon && (
                            <Icon
                                aria-hidden
                                sx={{
                                    fontSize: 20,
                                    mt: "1px",
                                    color: selected ? accent : "text.secondary",
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Box sx={{ minWidth: 0, pr: 2 }}>
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    fontFamily: "var(--font-body)",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    lineHeight: 1.3,
                                    color: "text.primary",
                                }}
                            >
                                {option.title}
                            </Box>
                            <Box
                                component="span"
                                id={descriptionId}
                                sx={{
                                    display: "block",
                                    fontFamily: "var(--font-body)",
                                    fontSize: 12,
                                    lineHeight: 1.35,
                                    color: "text.secondary",
                                    mt: "2px",
                                }}
                            >
                                {option.description}
                            </Box>
                        </Box>
                        {selected && (
                            <CheckCircleRoundedIcon
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    fontSize: 16,
                                    color: accent,
                                }}
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default ModeCards;
